import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useAuth } from './AuthContext';
import { loadSkiAreas } from './api/skiAreas';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [resorts, setResorts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('pm-theme') || 'light';
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showSignInHint, setShowSignInHint] = useState(false);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const { user, loading: authLoading, signInWithGoogle, signOut } = useAuth();

  // Once-per-session nudge for guests: 10s after auth resolves to "no user",
  // then visible for 10s. The session flag is set at show-time (not at mount),
  // so users who never sit on a page long enough still get one chance later.
  useEffect(() => {
    if (authLoading || user) return;
    if (sessionStorage.getItem('pm-signin-hint-shown') === '1') return;

    let hideTimer;
    const showTimer = setTimeout(() => {
      sessionStorage.setItem('pm-signin-hint-shown', '1');
      setShowSignInHint(true);
      hideTimer = setTimeout(() => setShowSignInHint(false), 10000);
    }, 10000);

    return () => {
      clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [user, authLoading]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('pm-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    loadSkiAreas().then((data) => {
      if (data?.features) setResorts(data.features);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const lower = query.toLowerCase();
    const matches = resorts
      .filter(f => f.properties.name?.toLowerCase().includes(lower))
      .slice(0, 8);
    setSearchResults(matches);
    setShowDropdown(matches.length > 0);
  };

  const handleSelectResort = (feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    setSearchQuery('');
    setShowDropdown(false);
    navigate('/', { state: { flyTo: { lat, lng, properties: feature.properties } } });
  };

  const isActive = (path) => location.pathname === path;

  const handleRandomResort = () => {
    if (resorts.length === 0) return;
    const feature = resorts[Math.floor(Math.random() * resorts.length)];
    handleSelectResort(feature);
  };

  const navItems = [
    { path: '/', label: 'Map', variant: 'primary' },
    { path: '/leaderboard', label: 'Leaderboard', variant: 'primary' },
    { path: '/about', label: 'About', variant: 'primary' },
  ];

  return (
    <header className="site-header">
      <div
        className="d-flex align-items-center justify-content-between px-3 px-md-4"
        style={{ height: '56px', maxWidth: '1400px', margin: '0 auto' }}
      >
        {/* Brand */}
        <Link to="/" className="text-decoration-none d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-2"
            style={{
              width: '36px',
              height: '36px',
              background: 'linear-gradient(135deg, #00d4ff 0%, #0a7ea8 100%)',
              flexShrink: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
            </svg>
          </div>
          <div style={{ lineHeight: 1.2 }}>
            <div className="fw-bold" style={{ fontSize: '1.05rem', color: 'var(--pm-text)' }}>
              PowderMaps
            </div>
            <div className="d-none d-md-block" style={{ fontSize: '0.72rem', color: 'var(--pm-text-muted)' }}>
              Global Ski Resort Snow Tracker
            </div>
          </div>
        </Link>

        {/* Search bar */}
        <div ref={searchRef} className="d-none d-md-block position-relative" style={{ flex: '0 1 320px' }}>
          <div className="position-relative">
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--pm-text-dimmed)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search resorts..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              style={{
                width: '100%',
                padding: '6px 12px 6px 34px',
                fontSize: '0.85rem',
                borderRadius: '8px',
                border: '1px solid var(--pm-border)',
                background: 'var(--pm-bg-elevated, #1a2736)',
                color: 'var(--pm-text, #e8edf2)',
                outline: 'none',
              }}
            />
          </div>
          {showDropdown && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                background: 'var(--pm-bg-surface, #162029)',
                border: '1px solid var(--pm-border)',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                zIndex: 9999,
                overflow: 'hidden',
              }}
            >
              {searchResults.map((feature, i) => (
                <button
                  key={feature.properties.id || i}
                  onClick={() => handleSelectResort(feature)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--pm-text, #e8edf2)',
                    fontSize: '0.84rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderBottom: i < searchResults.length - 1 ? '1px solid var(--pm-border)' : 'none',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--pm-bg-elevated, #1a2736)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pm-accent, #00d4ff)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                  </svg>
                  <span>{feature.properties.name}</span>
                  {feature.properties.region && (
                    <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--pm-text-muted)' }}>
                      {feature.properties.region}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop nav */}
        <nav className="d-none d-md-flex align-items-center gap-2">
          {navItems.map(({ path, label, variant }) => (
            <Link key={path} to={path} className="text-decoration-none">
              <Button
                variant={isActive(path) ? variant : `outline-${variant}`}
                size="sm"
                className="px-3"
                style={{
                  fontSize: '0.82rem',
                  borderRadius: '6px',
                  ...(isActive(path) ? { boxShadow: '0 0 12px var(--pm-accent-glow)' } : {})
                }}
              >
                {label}
              </Button>
            </Link>
          ))}
          <button
            onClick={handleRandomResort}
            className="theme-toggle-btn"
            aria-label="Random resort"
            title="Fly to random resort"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 3h5v5"/><path d="M4 20 21 3"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/>
            </svg>
          </button>
          <button
            onClick={toggleTheme}
            className="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          {/* User menu */}
          {!authLoading && (
            <div ref={userMenuRef} className="position-relative">
              {user ? (
                <>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="user-avatar-btn"
                    aria-label="User menu"
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="user-avatar user-avatar-fallback">
                        {(user.displayName || user.email || '?')[0].toUpperCase()}
                      </div>
                    )}
                  </button>
                  {userMenuOpen && (
                    <div className="user-dropdown">
                      <div className="user-dropdown-header">
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{user.displayName}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--pm-text-muted)' }}>{user.email}</div>
                      </div>
                      <Link
                        to="/profile"
                        className="user-dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                        </svg>
                        My Resorts
                      </Link>
                      <button
                        className="user-dropdown-item"
                        onClick={() => { signOut(); setUserMenuOpen(false); }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={signInWithGoogle}
                    className="user-avatar-btn"
                    aria-label="Sign in with Google"
                    title="Sign in"
                  >
                    <div className="user-avatar user-avatar-fallback" aria-hidden>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                  </button>
                  {showSignInHint && (
                    <button
                      type="button"
                      className="signin-hint"
                      onClick={() => { setShowSignInHint(false); signInWithGoogle(); }}
                    >
                      Sign in with Google
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="d-md-none btn btn-sm border-0"
          style={{ color: 'var(--pm-text-muted)' }}
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-label="Toggle navigation"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {mobileNavOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileNavOpen && (
        <div className="d-md-none border-top px-3 pb-3 pt-2" style={{ background: 'var(--pm-bg-surface)', boxShadow: 'var(--pm-shadow-md)' }}>
          <div className="d-flex flex-column gap-1">
            {/* Mobile search */}
            <div ref={searchRef} className="position-relative mb-2">
              <div className="position-relative">
                <svg
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--pm-text-dimmed)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search resorts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 34px',
                    fontSize: '0.9rem',
                    borderRadius: '8px',
                    border: '1px solid var(--pm-border)',
                    background: 'var(--pm-bg-elevated)',
                    color: 'var(--pm-text)',
                    outline: 'none',
                  }}
                />
              </div>
              {showDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    background: 'var(--pm-bg-surface)',
                    border: '1px solid var(--pm-border)',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    zIndex: 9999,
                    overflow: 'hidden',
                    maxHeight: '240px',
                    overflowY: 'auto',
                  }}
                >
                  {searchResults.map((feature, i) => (
                    <button
                      key={feature.properties.id || i}
                      onClick={() => { handleSelectResort(feature); setMobileNavOpen(false); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--pm-text)',
                        fontSize: '0.88rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderBottom: i < searchResults.length - 1 ? '1px solid var(--pm-border)' : 'none',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pm-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
                      </svg>
                      <span>{feature.properties.name}</span>
                      {feature.properties.region && (
                        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--pm-text-muted)' }}>
                          {feature.properties.region}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {navItems.map(({ path, label, variant }) => (
              <Link key={path} to={path} className="text-decoration-none" onClick={() => setMobileNavOpen(false)}>
                <Button
                  variant={isActive(path) ? variant : `outline-${variant}`}
                  size="sm"
                  className="w-100 text-start"
                  style={{ fontSize: '0.85rem', borderRadius: '6px' }}
                >
                  {label}
                </Button>
              </Link>
            ))}
            <Button
              variant="outline-primary"
              size="sm"
              className="w-100 text-start"
              style={{ fontSize: '0.85rem', borderRadius: '6px' }}
              onClick={() => { handleRandomResort(); setMobileNavOpen(false); }}
            >
              Random Resort
            </Button>
            <button
              onClick={toggleTheme}
              className="theme-toggle-btn w-100 mt-1"
              style={{ height: '34px', width: '100%' }}
            >
              {theme === 'dark' ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
            {/* Mobile user section */}
            {!authLoading && (
              <>
                <div style={{ borderTop: '1px solid var(--pm-border)', margin: '8px 0' }} />
                {user ? (
                  <>
                    <div className="d-flex align-items-center gap-2 px-1 py-1">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="" className="user-avatar" referrerPolicy="no-referrer" style={{ width: '28px', height: '28px' }} />
                      ) : (
                        <div className="user-avatar user-avatar-fallback" style={{ width: '28px', height: '28px', fontSize: '0.75rem' }}>
                          {(user.displayName || user.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <span style={{ fontSize: '0.85rem', color: 'var(--pm-text)' }}>{user.displayName}</span>
                    </div>
                    <Link to="/profile" className="text-decoration-none" onClick={() => setMobileNavOpen(false)}>
                      <Button variant="outline-primary" size="sm" className="w-100 text-start" style={{ fontSize: '0.85rem', borderRadius: '6px' }}>
                        My Resorts
                      </Button>
                    </Link>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="w-100 text-start"
                      style={{ fontSize: '0.85rem', borderRadius: '6px' }}
                      onClick={() => { signOut(); setMobileNavOpen(false); }}
                    >
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 text-start"
                    style={{ fontSize: '0.85rem', borderRadius: '6px' }}
                    onClick={() => { signInWithGoogle(); setMobileNavOpen(false); }}
                  >
                    Sign In with Google
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
